import React, { createContext, useContext, useMemo } from 'react';

export type Role = 'guest' | 'user' | 'partner' | 'internal' | 'admin';

interface AuthzValue {
  readonly role: Role;
  readonly permissions: string[];
  readonly isAdmin: boolean;
  readonly isInternal: boolean;
  readonly isPartner: boolean;
  readonly isUser: boolean;
  atLeast: (role: Role) => boolean;
  has: (permission: string) => boolean;
}

const RoleWeight: Record<Role, number> = {
  guest: 0,
  user: 1,
  partner: 2,
  internal: 3,
  admin: 4,
};

const defaultValue: AuthzValue = {
  role: 'guest',
  permissions: [],
  isAdmin: false,
  isInternal: false,
  isPartner: false,
  isUser: false,
  atLeast: () => false,
  has: () => false,
};

const AuthzContext = createContext<AuthzValue>(defaultValue);

export function AuthzProvider(props: {
  readonly role?: string;
  readonly permissions?: string[];
  readonly children: React.ReactNode;
}) {
  const role = (props.role as Role) || 'guest';
  const permissions = props.permissions || [];

  const value = useMemo<AuthzValue>(() => {
    const weight = RoleWeight[role] ?? 0;
    return {
      role,
      permissions,
      isAdmin: role === 'admin',
      isInternal: role === 'internal',
      isPartner: role === 'partner',
      isUser: role === 'user',
      atLeast: (min: Role) => weight >= (RoleWeight[min] ?? 99),
      has: (p: string) => permissions.includes(p),
    };
  }, [
    role,
    permissions,
  ]);

  return (
    <AuthzContext.Provider value={value}>
      {props.children}
    </AuthzContext.Provider>
  );
}

export function useAuthz(): AuthzValue {
  return useContext(AuthzContext);
}
